// =============================================================================
// UnleashedInspector — read a real record back from Unleashed as RAW JSON.
// =============================================================================
// Lives on Manage → DFV Services → Import Unleashed Data. Reads a live record via
// the unleashed-fetch Edge Function with { raw: true } so we see the EXACT field
// shapes Unleashed returns (addresses, StopCredit, bank, etc.) before wiring the
// auto-sync mappers — the Customer equivalent of the Sales Order Inspector.
// Read-only; nothing is written. (Contacts + Delivery-Address inspectors + the
// GUID-link utilities will join here — see docs/unleashed-auto-sync-plan.md.)
// =============================================================================
import React from "react";
import { getSupabaseClient } from "../../utils/supabase";
import { Btn, Card } from "../../utils/ui.jsx";

export function UnleashedInspector() {
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [json, setJson] = React.useState("");

  async function fetchCustomer() {
    setBusy(true);
    setError("");
    setJson("");
    try {
      const supa = getSupabaseClient();
      if (!supa) {
        setError("Could not reach the server.");
        return;
      }
      const { data, error: invErr } = await supa.functions.invoke("unleashed-fetch", {
        body: { resource: "Customers", customerCode: code.trim(), raw: true },
      });
      if (invErr) {
        setError(`Fetch failed: ${invErr.message}`);
        return;
      }
      if (!data || data.ok === false) {
        const st = data?.status ? ` (${data.status})` : "";
        setError(`Unleashed error: ${data?.error || "unknown"}${st}`);
        return;
      }
      // customerCode is a starts-with filter; the exact match is the shortest code,
      // but for inspection just show the first returned record.
      const first = (data.items || [])[0];
      if (!first) {
        setError("No Customer found with that code in Unleashed.");
        return;
      }
      setJson(JSON.stringify(first, null, 2));
    } catch (e) {
      setError(`Failed: ${e?.message || "error"}`);
    } finally {
      setBusy(false);
    }
  }

  const inputStyle = {
    border: "1px solid #CFD8DC",
    borderRadius: 6,
    padding: "8px 10px",
    fontSize: 13,
    fontFamily: "inherit",
    width: 240,
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 6px", fontSize: 16, color: "#00695C" }}>
        Inspect a Customer (raw Unleashed JSON)
      </h3>
      <p style={{ margin: "0 0 14px", fontSize: 13, color: "#607D79" }}>
        Read a real Customer back from Unleashed to see its exact field shapes (addresses,
        StopCredit, bank, sales group). Read-only — nothing is changed.
      </p>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Customer Code e.g. ZephyrQLD001"
          style={inputStyle}
        />
        <Btn onClick={fetchCustomer} disabled={busy || !code.trim()}>
          {busy ? "Fetching…" : "Fetch Customer JSON"}
        </Btn>
        <button
          type="button"
          onClick={function () {
            setCode("");
            setJson("");
            setError("");
          }}
          disabled={busy || (!code && !json && !error)}
          style={{
            background: "white",
            color: "#607D79",
            border: "1px solid #CFD8DC",
            borderRadius: 6,
            padding: "8px 16px",
            fontSize: 13,
            fontWeight: 600,
            fontFamily: "inherit",
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          Clear
        </button>
        {error && <span style={{ fontSize: 13, fontWeight: 600, color: "#C62828" }}>{error}</span>}
      </div>
      {json && (
        <pre
          style={{
            marginTop: 14,
            fontSize: 12,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            background: "#F7F9F9",
            border: "1px solid #E0EDEB",
            borderRadius: 8,
            padding: "12px 14px",
            maxHeight: 420,
            overflow: "auto",
            userSelect: "all",
          }}
        >
          {json}
        </pre>
      )}
    </Card>
  );
}
